import { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Banknote } from "lucide-react";
import { createWithdrawal } from "@/lib/afmApi";
import { fmtUSD, fmtDate } from "@/lib/format";

export const METHODS = [
  { key: "bank", label: "Transferencia bancaria", fields: [
    ["account_holder", "Titular de la cuenta"], ["bank_name", "Banco"],
    ["account_number", "Cuenta / IBAN / CLABE"], ["swift", "SWIFT / BIC"], ["country", "País"],
  ] },
  { key: "paypal", label: "PayPal", fields: [["paypal_email", "Email de PayPal"]] },
  { key: "crypto", label: "Cripto (USDT / USDC)", fields: [
    ["network", "Red (TRC20 / ERC20 / Polygon)"], ["wallet", "Dirección de billetera"],
  ] },
  { key: "pago_movil", label: "Bolívares · Pago Móvil", fields: [
    ["cedula", "Cédula"], ["phone", "Teléfono"], ["bank_ve", "Banco"],
  ] },
  { key: "transfer_bs", label: "Bolívares · Transferencia", fields: [
    ["account_holder", "Titular"], ["cedula", "Cédula"], ["bank_ve", "Banco"], ["account_number", "Número de cuenta"],
  ] },
  { key: "other", label: "Otro método", fields: [["note", "Detalles del método"]] },
];

const methodLabel = (k) => METHODS.find((m) => m.key === k)?.label || k;

const STATUS = {
  pending: { label: "Pendiente", cls: "border-amber-300 bg-amber-50 text-amber-800" },
  approved: { label: "Aprobado", cls: "border-blue-300 bg-blue-50 text-blue-700" },
  paid: { label: "Pagado", cls: "border-emerald-300 bg-emerald-50 text-emerald-700" },
  rejected: { label: "Rechazado", cls: "border-red-300 bg-red-50 text-red-700" },
};

export function WithdrawDialog({ open, onClose, userId, available, min = 50, onDone }) {
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("bank");
  const [details, setDetails] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) { setAmount(""); setMethod("bank"); setDetails({}); }
  }, [open]);

  const cfg = METHODS.find((m) => m.key === method);
  const amt = Number(amount || 0);
  const fieldsFilled = cfg.fields.every(([k]) => (details[k] || "").trim());
  const valid = amt >= min && amt <= available && fieldsFilled;

  const submit = async () => {
    if (amt < min) { toast.error(`El monto mínimo de retiro es USD ${min}.`); return; }
    if (amt > available) { toast.error("El monto supera tu saldo disponible."); return; }
    if (!fieldsFilled) { toast.error("Completa todos los datos del método de pago."); return; }
    setSaving(true);
    try {
      const detailsStr = cfg.fields.map(([k, label]) => `${label}: ${details[k] || ""}`).join(" · ");
      await createWithdrawal({ user_id: userId, amount_usd: amt, method, details: detailsStr });
      toast.success("Solicitud de retiro enviada. Te avisaremos cuando se procese.");
      onDone();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo registrar el retiro");
    } finally { setSaving(false); }
  };

  if (!open) return null;
  const inputCls = "w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-black";

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4" data-testid="withdraw-dialog">
      <div className="my-8 w-full max-w-lg rounded-2xl border border-slate-300 bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <h2 className="font-serif text-xl font-bold text-black">Retirar fondos</h2>
          <button onClick={onClose} data-testid="close-withdraw-dialog"><X className="h-5 w-5 text-slate-400 hover:text-black" /></button>
        </div>
        <div className="space-y-5 p-6">
          <div className="rounded-lg bg-slate-50 px-4 py-3 text-sm text-slate-600">
            Disponible: <span className="font-mono font-bold text-black">{fmtUSD(available)}</span> · Mínimo <span className="font-semibold">USD {min}</span>
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-600">Monto a retirar (USD)</label>
            <input data-testid="withdraw-amount" type="number" min={min} max={available} value={amount} onChange={(e) => setAmount(e.target.value)} placeholder={`${min}`} className={inputCls} />
            {amount && amt < min && <p className="mt-1 text-xs text-red-600">Mínimo USD {min}.</p>}
            {amount && amt > available && <p className="mt-1 text-xs text-red-600">Supera el saldo disponible.</p>}
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-600">Método de pago</label>
            <select data-testid="withdraw-method" value={method} onChange={(e) => { setMethod(e.target.value); setDetails({}); }} className={inputCls}>
              {METHODS.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
            </select>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            {cfg.fields.map(([k, label]) => (
              <div key={k} className={k === "note" ? "sm:col-span-2" : ""}>
                <label className="mb-1 block text-xs font-medium text-slate-500">{label}</label>
                <input data-testid={`withdraw-field-${k}`} value={details[k] || ""} onChange={(e) => setDetails((d) => ({ ...d, [k]: e.target.value }))} className={inputCls} />
              </div>
            ))}
          </div>

          <p className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
            Tu solicitud se enviará al equipo de Always Fresh Music para su revisión y pago. Podrás ver el estado en el historial.
          </p>
        </div>
        <div className="flex justify-end gap-3 border-t border-slate-200 px-6 py-4">
          <button onClick={onClose} className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium hover:border-black">Cancelar</button>
          <button onClick={submit} disabled={!valid || saving} data-testid="submit-withdraw-button" className="rounded-lg bg-black px-5 py-2 text-sm font-semibold text-white disabled:opacity-50">
            {saving ? "Enviando…" : "Solicitar retiro"}
          </button>
        </div>
      </div>
    </div>
  );
}

export function WithdrawalHistory({ items = [] }) {
  if (!items.length) return null;

  return (
    <div className="rounded-xl border border-slate-200 bg-white" data-testid="withdrawal-history">
      <div className="border-b border-slate-200 px-5 py-4">
        <h2 className="font-serif text-xl font-bold tracking-tight text-black">Solicitudes de retiro</h2>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
              <th className="px-5 py-3 font-semibold">Fecha</th>
              <th className="px-5 py-3 font-semibold">Método</th>
              <th className="px-5 py-3 text-right font-semibold">Monto</th>
              <th className="px-5 py-3 text-center font-semibold">Estado</th>
            </tr>
          </thead>
          <tbody>
            {items.map((w) => {
              const st = STATUS[w.status] || { label: w.status || "—", cls: "border-slate-300 bg-slate-50 text-slate-600" };
              return (
                <tr key={w.id} className="border-t border-slate-100" data-testid={`withdrawal-row-${w.id}`}>
                  <td className="px-5 py-3 text-slate-600">{fmtDate(w.created_at)}</td>
                  <td className="px-5 py-3 font-medium text-slate-800">{methodLabel(w.method)}</td>
                  <td className="px-5 py-3 text-right font-mono font-semibold tabular-nums text-black">{fmtUSD(w.amount_usd)}</td>
                  <td className="px-5 py-3 text-center">
                    <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-semibold ${st.cls}`}>
                      <Banknote className="h-3 w-3" /> {st.label}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
